/**
 * @file ChallengeInfoTab.jsx
 * @description Info tab component for MaterialChallenge
 */

import React from "react";
import { formatChapterReference } from "../../../utils/bible_language_utils";
import { capitalizeBook } from "../../../utils/bible_utils";
import { getBibleLanguageFromCookie } from "../../../utils/theme_utils";
import { BIBLE_LANGUAGES } from "../../../constants";
import { parseDateList, formatDateString, isValidDate } from "../../../utils/dateUtils";
import { Icon } from "../../Icon";

export const ChallengeInfoTab = ({
  challengeData,
  datesToChapters,
  userChallengeData,
  onDateClick
}) => {
  const skipDates = parseDateList(challengeData.skip_dates || "");
  const bibleLanguage = getBibleLanguageFromCookie() || BIBLE_LANGUAGES.ENGLISH;

  // Helper function to get completion status for a specific day
  const getCompletionStatus = (dayIndex) => {
    if (!userChallengeData || !userChallengeData.progress) {
      return 'incomplete';
    }
    
    const progress = userChallengeData.progress;
    if (dayIndex >= progress.length) {
      return 'incomplete';
    }
    
    return progress[dayIndex] === '1' ? 'complete' : 'incomplete';
  };
  
  return (
    <div style={{ padding: '16px', overflow: 'auto', height: '100%' }}>
      <md-elevated-card style={{ padding: '16px', marginBottom: '16px'}}>
        <h3 style={{ margin: '0 0 8px 0', color: 'var(--md-sys-color-on-surface)' }}>
          {challengeData.challenge_name}
        </h3>
        <p style={{ color: 'var(--md-sys-color-on-surface-variant)', margin: '0' }}>
          <strong>Books:</strong> {challengeData.books?.map(capitalizeBook).join(', ')}
        </p>
        <p style={{ color: 'var(--md-sys-color-on-surface-variant)', margin: '8px 0 0 0' }}>
          <strong>Period:</strong> {challengeData.start_date} to {challengeData.end_date}
        </p>
        <p style={{ color: 'var(--md-sys-color-on-surface-variant)', margin: '8px 0 0 0' }}>
          <strong>Days:</strong> {challengeData.days?.join(', ')}
        </p>
        <p style={{ color: 'var(--md-sys-color-on-surface-variant)', margin: '8px 0 0 0' }}>
          <strong>Visibility:</strong> {challengeData.is_public ? 'Public' : 'Private'}
        </p>
        <p style={{ color: 'var(--md-sys-color-on-surface-variant)', margin: '8px 0 0 0' }}>
          <strong>Total Days:</strong> {datesToChapters.length}
        </p>
        {skipDates.length > 0 && (
          <p style={{ color: 'var(--md-sys-color-on-surface-variant)', margin: '8px 0 0 0' }}>
            <strong>Skip Dates:</strong> {skipDates.join(', ')}
          </p>
        )}
        {/* Reading Schedule */}
        {datesToChapters && datesToChapters.length > 0 && (
          <md-elevated-card style={{ padding: '16px 0 0 0', marginBottom: '16px' }}>
            <h3 style={{
              color: 'var(--md-sys-color-on-surface)',
              fontSize: '20px',
              fontWeight: '500',
              margin: '0 0 16px 0'
            }}>
              Reading Schedule
            </h3>
            <div style={{
              maxHeight: '300px',
              overflow: 'auto',
              border: '1px solid var(--md-sys-color-outline-variant)',
              borderRadius: '8px'
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ 
                    backgroundColor: 'var(--md-sys-color-surface-container)',
                    position: 'sticky',
                    top: 0
                  }}>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left',
                      color: 'var(--md-sys-color-on-surface)',
                      fontWeight: '500'
                    }}>
                      Day
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left',
                      color: 'var(--md-sys-color-on-surface)',
                      fontWeight: '500'
                    }}>
                      Chapters
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'center',
                      color: 'var(--md-sys-color-on-surface)',
                      fontWeight: '500',
                      width: '60px'
                    }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {datesToChapters.map((dateToChapter, index) => {
                    const completionStatus = getCompletionStatus(index);
                    const isComplete = completionStatus === 'complete';
                    
                    return (
                      <tr key={index} style={{
                        borderTop: index > 0 ? '1px solid var(--md-sys-color-outline-variant)' : 'none',
                        cursor: onDateClick ? 'pointer' : 'default'
                      }}
                      onClick={() => onDateClick && onDateClick(index)}
                      onMouseEnter={(e) => {
                        if (onDateClick) {
                          e.currentTarget.style.backgroundColor = 'var(--md-sys-color-surface-container)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (onDateClick) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                      >
                        <td style={{ 
                          padding: '12px',
                          color: onDateClick ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface)',
                          fontWeight: onDateClick ? '500' : '400'
                        }}>
                          {isValidDate(dateToChapter[0]) ? formatDateString(dateToChapter[0]) : 'Invalid Date'}
                        </td>
                        <td style={{ 
                          padding: '12px',
                          color: 'var(--md-sys-color-on-surface-variant)'
                        }}>
                          {dateToChapter[1]
                            .map(chapter => formatChapterReference(chapter[0], chapter[1], bibleLanguage))
                            .join(", ")}
                        </td>
                        <td style={{ 
                          padding: '12px',
                          textAlign: 'center'
                        }}>
                          <Icon
                            name={isComplete ? "check" : "incomplete"}
                            style={{
                              color: isComplete ? 'var(--md-sys-color-tertiary)' : 'var(--md-sys-color-error)',
                              fontSize: '20px'
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </md-elevated-card>
        )}
      </md-elevated-card>
    </div>
  );
};
